import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronRight, faRotateRight } from '@fortawesome/free-solid-svg-icons'
import { api } from '../services'
import { VirtualizedMessageTable } from '../components/VirtualizedMessageTable'
import {
  TimeRangeNavigation,
  type TimeRange,
  type MessageCategory,
  ALL_MESSAGE_CATEGORIES,
  categoriesToPrefixes,
} from '../components/TimeRangeNavigation'
import { UnitCategoryDropdowns } from '../components/UnitCategoryDropdowns'
import { useDeviceUrlSync } from '../hooks/useDeviceUrlSync'
import { useResponsiveLayout, LAYOUT_DIMENSIONS } from '../hooks/useResponsiveLayout'
import { useSensorConfigs } from '../hooks/useSensorConfigs'
import { useMessageUnitOverrides } from '../hooks/useMessageUnitOverrides'
import { cn } from '../shared/utils/cn'
import { UnitCategoryId } from '../common/enums'
import { REFRESH_OVERLAP_SECONDS, isLiveRange, mergeNewMessages } from '../utils/messageRefresh'

// Type definition for message (matching TMessageSigfoxOut from legacy API)
interface Message {
  MSG_RAW_ID: number
  SIGFOX_SNUM: number
  SIGFOX_TIME: number
  MSG_CONTENT: string
}

// Local utility to determine message type from hex content
function determineMessageType(messageContent: string): string {
  if (!messageContent || messageContent.length < 2) {
    return 'Unknown'
  }

  const byte = messageContent.slice(0, 2)
  const messageType = parseInt(byte, 16)

  switch (messageType) {
    case 0x04:
      return 'TestMessage'
    case 0x05:
      return 'SNTS'
    case 0x06:
      return 'SCHD'
    case 0x07:
      return 'DeviceConfig1'
    case 0x08:
      return 'DeviceConfig2'
    case 0x09:
      return 'DeviceConfig3'
    case 0x0a:
      return 'DeviceConfig4'
    case 0x0b:
      return 'DeviceConfig5'
    case 0x0c:
      return 'SensorConfig1'
    default:
      return 'Unknown'
  }
}

const MESSAGE_TYPES = [
  'TestMessage',
  'DeviceConfig1',
  'DeviceConfig2',
  'DeviceConfig3',
  'DeviceConfig4',
  'DeviceConfig5',
  'SensorConfig1',
  'SNTS',
  'SCHD',
]

const SENSOR_PORTS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'C3']

// How often Live checks for new messages
const LIVE_POLL_MS = 60 * 1000

export default function MessagesPage(): JSX.Element {
  // Use custom hook for URL/state synchronization
  const { selectedDevice, sigfoxId, sigfoxIdFromUrl, isValidSigfoxId, urlTimeRange, updateTimeRangeInUrl } = useDeviceUrlSync()
  const [searchParams, setSearchParams] = useSearchParams()

  // Get responsive layout info for mobile-aware sizing
  const layout = useResponsiveLayout()

  // Fetch sensor configs for the selected device
  const { data: sensorConfigData } = useSensorConfigs(sigfoxId || '', undefined, true, !!sigfoxId)

  // Transform configs array to a map keyed by port name (A1, A2, B1, etc.)
  const sensorConfigMap = useMemo(() => {
    const configs = sensorConfigData?.configs || sensorConfigData?.data || []
    if (!Array.isArray(configs)) {
      // If already a map, use as-is
      return configs as Record<string, any>
    }
    // Convert array to map keyed by port name
    const portNames = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'C3']
    const map: Record<string, any> = {}
    configs.forEach((config: any) => {
      const sensorNumber = config.sensorNumber || config.sensorId
      if (sensorNumber >= 1 && sensorNumber <= 7) {
        map[portNames[sensorNumber - 1]] = config
      }
    })
    return map
  }, [sensorConfigData])

  // Unit override state for message display
  const { unitOverrides, setUnitOverride } = useMessageUnitOverrides()

  // Extract unique unit categories from sensor configs + always include Temperature
  // Temperature is built into SCHD messages and can always be converted
  const presentUnitCategories = useMemo(() => {
    const categories = new Set<number>()
    // Always include Temperature (built into SCHD messages)
    categories.add(UnitCategoryId.Temperature)
    // Add categories from sensor configs
    Object.values(sensorConfigMap).forEach((config: any) => {
      if (config?.unitCategoryId && config.unitCategoryId > 0) {
        categories.add(config.unitCategoryId)
      }
    })
    return Array.from(categories).sort((a, b) => a - b)
  }, [sensorConfigMap])

  // Parse filters from URL - we store what's HIDDEN, not what's shown
  // No param = show all, param lists excluded items
  const urlHiddenTypes = searchParams.get('hideTypes')
  const urlHiddenPorts = searchParams.get('hidePorts')
  // API categories: no param = all selected, param lists which categories to fetch
  const urlCategories = searchParams.get('categories')

  const [searchFilter, setSearchFilter] = useState('')
  const [selectedMessageTypes, setSelectedMessageTypes] = useState<Set<string>>(() => {
    if (urlHiddenTypes) {
      const hiddenTypes = urlHiddenTypes.split(',').filter((t) => MESSAGE_TYPES.includes(t))
      // Start with all, remove hidden ones
      const selected = new Set(MESSAGE_TYPES)
      hiddenTypes.forEach((t) => selected.delete(t))
      return selected
    }
    return new Set(MESSAGE_TYPES)
  })
  const [selectedSensorPorts, setSelectedSensorPorts] = useState<Set<string>>(() => {
    if (urlHiddenPorts) {
      const hiddenPorts = urlHiddenPorts.split(',').filter((p) => SENSOR_PORTS.includes(p))
      // Start with all, remove hidden ones
      const selected = new Set(SENSOR_PORTS)
      hiddenPorts.forEach((p) => selected.delete(p))
      return selected
    }
    return new Set(SENSOR_PORTS)
  })
  // API-level category filtering - determines which message types are fetched from the server
  // No param = all categories, param = only those listed
  const [selectedApiCategories, setSelectedApiCategories] = useState<Set<MessageCategory>>(() => {
    if (urlCategories) {
      const categories = urlCategories.split(',').filter((c) =>
        ALL_MESSAGE_CATEGORIES.includes(c as MessageCategory)
      ) as MessageCategory[]
      return new Set<MessageCategory>(categories)
    }
    return new Set<MessageCategory>(ALL_MESSAGE_CATEGORIES)
  })
  const [filtersExpanded, setFiltersExpanded] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [oldestTimestamp, setOldestTimestamp] = useState<number | null>(null)
  const [newestTimestamp, setNewestTimestamp] = useState<number | null>(null)
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc')

  // Refresh / Live: fetch only messages newer than the newest one loaded
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isLive, setIsLive] = useState(false)
  const [lastChecked, setLastChecked] = useState<{ at: number; added: number } | null>(null)
  // Upper bound (seconds) of what has been asked for; refresh moves it to now so
  // oldest-first "load more" can reach messages that arrived after the first fetch
  const [rangeTop, setRangeTop] = useState<number | null>(null)
  // Bumped by every full fetch so a refresh started before it is discarded
  const fetchGenerationRef = useRef(0)

  // Initialize time range from URL or default to 28 days
  const [timeRange, setTimeRange] = useState<TimeRange>(() => {
    if (urlTimeRange) {
      const days = Math.ceil((urlTimeRange.to - urlTimeRange.from) / (24 * 3600 * 1000))
      return {
        preset: 'month',
        from: urlTimeRange.from,
        to: urlTimeRange.to,
        label: `${days} day${days !== 1 ? 's' : ''}`,
      }
    }
    // Default: 28 days ending now
    const now = Date.now()
    const from = now - 28 * 24 * 3600 * 1000
    return { preset: 'month', from, to: now, label: '28 days' }
  })

  // DOM-measured header height for accurate table sizing
  const headerRef = useRef<HTMLDivElement>(null)
  const [measuredHeaderHeight, setMeasuredHeaderHeight] = useState(0)

  useEffect(() => {
    if (!headerRef.current) return
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setMeasuredHeaderHeight(entry.contentRect.height)
      }
    })
    observer.observe(headerRef.current)
    return () => observer.disconnect()
  }, [])

  const TABLE_FOOTER_HEIGHT = 32
  const TABLE_HEIGHT_BUFFER = 12
  const reservedHeight = measuredHeaderHeight + TABLE_FOOTER_HEIGHT + TABLE_HEIGHT_BUFFER

  // Auto-collapse time range header on mobile/tablet (not desktop)
  const canCollapseHeader = !layout.isDesktop
  const [headerCollapsed, setHeaderCollapsed] = useState(canCollapseHeader)

  // Sync collapse state when layout changes (e.g., orientation change)
  useEffect(() => {
    if (!layout.isDesktop) {
      setHeaderCollapsed(true)
    }
  }, [layout.isDesktop])

  // Handle time range changes - sync to URL
  const handleRangeChange = useCallback(
    (newRange: TimeRange) => {
      setTimeRange(newRange)
      if (newRange.from && newRange.to) {
        updateTimeRangeInUrl(newRange.from, newRange.to)
      }
    },
    [updateTimeRangeInUrl]
  )

  // Initialize URL with default time range when device is selected but no from/to in URL
  useEffect(() => {
    if (sigfoxId && !urlTimeRange && timeRange.from && timeRange.to) {
      updateTimeRangeInUrl(timeRange.from, timeRange.to)
    }
  }, [sigfoxId, urlTimeRange, timeRange.from, timeRange.to, updateTimeRangeInUrl])

  // Sync URL time range changes back to state (e.g., browser back/forward)
  useEffect(() => {
    if (urlTimeRange && urlTimeRange.from && urlTimeRange.to) {
      // Only update if different from current range
      if (urlTimeRange.from !== timeRange.from || urlTimeRange.to !== timeRange.to) {
        const days = Math.ceil((urlTimeRange.to - urlTimeRange.from) / (24 * 3600 * 1000))
        setTimeRange({
          preset: 'month',
          from: urlTimeRange.from,
          to: urlTimeRange.to,
          label: `${days} day${days !== 1 ? 's' : ''}`,
        })
      }
    }
  }, [urlTimeRange]) // eslint-disable-line react-hooks/exhaustive-deps

  // Sync filters to URL when they change - store what's HIDDEN
  const updateFiltersInUrl = useCallback(
    (messageTypes: Set<string>, sensorPorts: Set<string>) => {
      const newParams = new URLSearchParams(searchParams)

      // Calculate hidden types (all types minus selected)
      const hiddenTypes = MESSAGE_TYPES.filter((t) => !messageTypes.has(t))
      if (hiddenTypes.length > 0) {
        newParams.set('hideTypes', hiddenTypes.join(','))
      } else {
        newParams.delete('hideTypes')
      }

      // Calculate hidden ports (all ports minus selected)
      const hiddenPorts = SENSOR_PORTS.filter((p) => !sensorPorts.has(p))
      if (hiddenPorts.length > 0) {
        newParams.set('hidePorts', hiddenPorts.join(','))
      } else {
        newParams.delete('hidePorts')
      }

      setSearchParams(newParams, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  // Sync API categories to URL - no param = all, param = selected categories
  const updateApiCategoriesInUrl = useCallback(
    (categories: Set<MessageCategory>) => {
      const newParams = new URLSearchParams(searchParams)

      // If all categories selected, remove param; otherwise list selected ones
      if (categories.size === ALL_MESSAGE_CATEGORIES.length) {
        newParams.delete('categories')
      } else if (categories.size > 0) {
        newParams.set('categories', Array.from(categories).join(','))
      } else {
        // Edge case: nothing selected - keep param empty to indicate explicit empty selection
        newParams.set('categories', '')
      }

      setSearchParams(newParams, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  // Handle API category changes
  const handleApiCategoriesChange = useCallback(
    (categories: Set<MessageCategory>) => {
      setSelectedApiCategories(categories)
      updateApiCategoriesInUrl(categories)
    },
    [updateApiCategoriesInUrl]
  )

  // Fetch messages based on time range and API categories
  useEffect(() => {
    if (!sigfoxId || !timeRange.from || !timeRange.to) return

    const generation = ++fetchGenerationRef.current

    const fetchMessages = async () => {
      setIsLoading(true)
      setError(null)
      setHasMore(true)
      setLastChecked(null)

      try {
        // Convert milliseconds to seconds for API (already checked for undefined above)
        const toTimestamp = Math.floor(timeRange.to! / 1000)
        const fromTimestamp = Math.floor(timeRange.from! / 1000)

        // Convert selected categories to message type prefixes for API filtering
        const apiPrefixes = selectedApiCategories.size === ALL_MESSAGE_CATEGORIES.length
          ? undefined // All categories selected = fetch all types
          : categoriesToPrefixes(selectedApiCategories)

        const response = await api.getRawSigfoxMessages(
          sigfoxId as string,
          toTimestamp,
          fromTimestamp,
          sortOrder,
          500,
          apiPrefixes
        )

        if (generation !== fetchGenerationRef.current) return

        const fetchedMessages = response.data || []
        setMessages(fetchedMessages)
        setRangeTop(Math.min(toTimestamp, Math.floor(Date.now() / 1000)))

        // Track oldest and newest timestamps for pagination
        if (fetchedMessages.length > 0) {
          const oldest = Math.min(...fetchedMessages.map((m: Message) => m.SIGFOX_TIME))
          const newest = Math.max(...fetchedMessages.map((m: Message) => m.SIGFOX_TIME))
          setOldestTimestamp(oldest)
          setNewestTimestamp(newest)
          setHasMore(fetchedMessages.length === 500)
        } else {
          setOldestTimestamp(null)
          setNewestTimestamp(null)
          setHasMore(false)
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load messages')
        setMessages([])
        setHasMore(false)
      } finally {
        setIsLoading(false)
      }
    }

    fetchMessages()
  }, [sigfoxId, timeRange.from, timeRange.to, sortOrder, selectedApiCategories])

  // Load more messages (pagination) - extends beyond the current time range
  const handleLoadMore = useCallback(async () => {
    if (!sigfoxId || !timeRange.from || !timeRange.to || isLoadingMore || !hasMore) return

    // For desc (newest first), we load older messages (before oldestTimestamp)
    // For asc (oldest first), we load newer messages (after newestTimestamp)
    const needsOldest = sortOrder === 'desc'
    if (needsOldest && !oldestTimestamp) return
    if (!needsOldest && !newestTimestamp) return

    setIsLoadingMore(true)

    try {
      // Determine pagination bounds based on sort order
      let toTimestamp: number | undefined
      let fromTimestamp: number | undefined

      if (sortOrder === 'desc') {
        // Loading older messages: fetch from range start to just before oldest we have
        toTimestamp = oldestTimestamp! - 1
        fromTimestamp = Math.floor(timeRange.from / 1000)
      } else {
        // Loading newer messages: fetch from just after newest we have to range end
        fromTimestamp = newestTimestamp! + 1
        toTimestamp = Math.max(Math.floor(timeRange.to / 1000), rangeTop ?? 0)
      }

      // Use same API category filtering as main fetch
      const apiPrefixes = selectedApiCategories.size === ALL_MESSAGE_CATEGORIES.length
        ? undefined
        : categoriesToPrefixes(selectedApiCategories)

      const response = await api.getRawSigfoxMessages(
        sigfoxId as string,
        toTimestamp,
        fromTimestamp,
        sortOrder,
        500,
        apiPrefixes
      )

      const fetchedMessages = response.data || []

      if (fetchedMessages.length > 0) {
        // Append to end (pagination continues in sort direction)
        setMessages((prev) => [...prev, ...fetchedMessages])

        // Update boundary timestamps
        if (sortOrder === 'desc') {
          const oldest = Math.min(...fetchedMessages.map((m: Message) => m.SIGFOX_TIME))
          setOldestTimestamp(oldest)
        } else {
          const newest = Math.max(...fetchedMessages.map((m: Message) => m.SIGFOX_TIME))
          setNewestTimestamp(newest)
        }

        setHasMore(fetchedMessages.length === 500)
      } else {
        setHasMore(false)
      }
    } catch (err: any) {
      console.error('Failed to load more messages:', err)
      setHasMore(false)
    } finally {
      setIsLoadingMore(false)
    }
  }, [sigfoxId, timeRange.from, timeRange.to, oldestTimestamp, newestTimestamp, isLoadingMore, hasMore, sortOrder, selectedApiCategories, rangeTop])

  const canRefresh = !!sigfoxId && isLiveRange(timeRange.to)

  // Fetch messages newer than the newest loaded one (with a small overlap for
  // late arrivals) up to now, and add them at the newest end of the list
  const handleRefresh = useCallback(async () => {
    if (!sigfoxId || !timeRange.from || isLoading || isRefreshing) return

    const now = Math.floor(Date.now() / 1000)

    // Oldest-first with more pages to load: the newest end isn't loaded yet, so
    // just let "load more" reach up to now
    if (sortOrder === 'asc' && hasMore) {
      setRangeTop(now)
      setLastChecked({ at: Date.now(), added: 0 })
      return
    }

    const generation = fetchGenerationRef.current
    setIsRefreshing(true)
    try {
      const apiPrefixes = selectedApiCategories.size === ALL_MESSAGE_CATEGORIES.length
        ? undefined
        : categoriesToPrefixes(selectedApiCategories)
      const since = newestTimestamp ?? Math.floor(timeRange.from / 1000)
      const response = await api.getRawSigfoxMessages(
        sigfoxId as string,
        now,
        Math.max(since - REFRESH_OVERLAP_SECONDS, Math.floor(timeRange.from / 1000)),
        sortOrder,
        500,
        apiPrefixes
      )
      if (generation !== fetchGenerationRef.current) return

      const { messages: merged, added } = mergeNewMessages(messages, (response.data || []) as Message[], sortOrder)
      if (added.length > 0) {
        setMessages(merged)
        setNewestTimestamp(Math.max(newestTimestamp ?? 0, ...added.map(m => m.SIGFOX_TIME)))
        if (oldestTimestamp === null) setOldestTimestamp(Math.min(...added.map(m => m.SIGFOX_TIME)))
      }
      setRangeTop(now)
      setLastChecked({ at: Date.now(), added: added.length })
    } catch (err: any) {
      console.error('Failed to refresh messages:', err)
    } finally {
      setIsRefreshing(false)
    }
  }, [sigfoxId, timeRange.from, isLoading, isRefreshing, sortOrder, hasMore, selectedApiCategories, newestTimestamp, oldestTimestamp, messages])

  // Live: check every minute while the tab is visible, and once on switching on
  const refreshRef = useRef(handleRefresh)
  refreshRef.current = handleRefresh
  useEffect(() => {
    if (!isLive || !canRefresh) return
    refreshRef.current()
    const timer = window.setInterval(() => {
      if (!document.hidden) refreshRef.current()
    }, LIVE_POLL_MS)
    return () => window.clearInterval(timer)
  }, [isLive, canRefresh])

  // Get message types that are actually found in current results
  const foundMessageTypes = useMemo(() => {
    const foundTypes = new Set<string>()
    messages.forEach((message: Message) => {
      foundTypes.add(determineMessageType(message.MSG_CONTENT))
    })
    return foundTypes
  }, [messages])

  // Filter messages based on display filters and search text
  const filteredMessages = useMemo(() => {
    let filtered = messages

    // Filter by selected message types (display filter)
    filtered = filtered.filter((message: Message) => {
      const messageType = determineMessageType(message.MSG_CONTENT)
      return selectedMessageTypes.has(messageType)
    })

    // Filter by search text
    if (searchFilter.trim()) {
      const filter = searchFilter.toLowerCase()
      filtered = filtered.filter((message: Message) => {
        const messageType = determineMessageType(message.MSG_CONTENT).toLowerCase()
        const content = message.MSG_CONTENT.toLowerCase()
        const sigfoxNum = message.SIGFOX_SNUM.toString().toLowerCase()

        return messageType.includes(filter) || content.includes(filter) || sigfoxNum.includes(filter)
      })
    }

    return filtered
  }, [messages, selectedMessageTypes, searchFilter])

  const toggleMessageType = (messageType: string) => {
    const newSet = new Set(selectedMessageTypes)
    if (newSet.has(messageType)) {
      newSet.delete(messageType)
    } else {
      newSet.add(messageType)
    }
    setSelectedMessageTypes(newSet)
    updateFiltersInUrl(newSet, selectedSensorPorts)
  }

  const toggleSensorPort = (sensorPort: string) => {
    const newSet = new Set(selectedSensorPorts)
    if (newSet.has(sensorPort)) {
      newSet.delete(sensorPort)
    } else {
      newSet.add(sensorPort)
    }
    setSelectedSensorPorts(newSet)
    updateFiltersInUrl(selectedMessageTypes, newSet)
  }

  // Calculate height accounting for bottom tabs on mobile
  const containerHeight = layout.showBottomTabs
    ? `calc(100vh - ${LAYOUT_DIMENSIONS.BOTTOM_TAB_HEIGHT}px)`
    : '100vh'

  return (
    <div className="flex flex-col overflow-hidden" style={{ height: containerHeight }}>
      {/* Fixed Header Section */}
      <div ref={headerRef} className="flex-shrink-0 bg-white shadow-sm border-b space-y-1 p-1">
        {/* Time Range Navigation - collapsible on small viewports */}
        {headerCollapsed ? (
          <button
            onClick={() => setHeaderCollapsed(false)}
            className={cn(
              'w-full flex items-center justify-between px-2 py-1 bg-white rounded-lg shadow-sm border text-sm',
              !selectedDevice && 'opacity-50 pointer-events-none'
            )}
          >
            <span className="text-gray-600">
              {timeRange.from && timeRange.to
                ? `${new Date(timeRange.from).toLocaleDateString()} – ${new Date(timeRange.to).toLocaleDateString()}`
                : timeRange.label || '28 days'}
            </span>
            <div className="flex items-center gap-2">
              {messages.length > 0 && (
                <span className="font-bold text-gray-800">{messages.length} msg</span>
              )}
              {isLoading && <span className="text-blue-600">Loading...</span>}
              <span className="text-xs text-blue-600 font-medium">Edit</span>
            </div>
          </button>
        ) : (
          <div
            className={cn(
              'bg-white rounded-lg shadow-sm border px-2 py-1',
              !selectedDevice && 'opacity-50 pointer-events-none'
            )}
          >
            <div className="flex items-center justify-between flex-wrap gap-1">
              <TimeRangeNavigation
                currentRange={timeRange}
                onRangeChange={handleRangeChange}
                showCategoryFilters={true}
                selectedCategories={selectedApiCategories}
                onCategoriesChange={handleApiCategoriesChange}
              />
              <div className="flex items-center gap-2">
                <RefreshControls
                  canRefresh={canRefresh}
                  isRefreshing={isRefreshing}
                  isLive={isLive}
                  onRefresh={handleRefresh}
                  onLiveChange={setIsLive}
                  lastChecked={lastChecked}
                />
                {messages.length > 0 && (
                  <span className="font-bold text-gray-800 text-sm">{messages.length} messages</span>
                )}
                {isLoading && <span className="text-blue-600 text-sm">Loading...</span>}
                {canCollapseHeader && (
                  <button
                    onClick={() => setHeaderCollapsed(true)}
                    className="text-xs text-gray-500 hover:text-gray-700 whitespace-nowrap"
                  >
                    Collapse
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Filters - Combined Message Types and Sensor Ports */}
        <div
          className={cn(
            'bg-white rounded-lg shadow-sm border flex-shrink-0',
            !selectedDevice && 'opacity-50 pointer-events-none'
          )}
        >
          <div className="px-2 py-1 space-y-1">
            {/* Collapsible Header with Search */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center space-x-2 flex-1">
                <button
                  onClick={() => setFiltersExpanded(!filtersExpanded)}
                  className="flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors flex-shrink-0"
                >
                  <FontAwesomeIcon icon={filtersExpanded ? faChevronDown : faChevronRight} className="h-3 w-3" />
                </button>
                <input
                  type="text"
                  placeholder="Search loaded messages..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="flex-1 px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex items-center gap-3">
                {/* Reset button - always visible, disabled when no filters applied */}
                <button
                  onClick={() => {
                    setSelectedMessageTypes(new Set(MESSAGE_TYPES))
                    setSelectedSensorPorts(new Set(SENSOR_PORTS))
                    updateFiltersInUrl(new Set(MESSAGE_TYPES), new Set(SENSOR_PORTS))
                  }}
                  disabled={
                    selectedMessageTypes.size === MESSAGE_TYPES.length &&
                    selectedSensorPorts.size === SENSOR_PORTS.length
                  }
                  className={cn(
                    'px-2 py-1 text-xs font-medium rounded border transition-colors whitespace-nowrap',
                    selectedMessageTypes.size === MESSAGE_TYPES.length &&
                      selectedSensorPorts.size === SENSOR_PORTS.length
                      ? 'text-gray-400 border-gray-200 bg-gray-50 cursor-not-allowed'
                      : 'text-blue-600 border-blue-300 hover:text-blue-800 hover:bg-blue-50'
                  )}
                >
                  Reset Filters
                </button>
                <div className="text-sm text-gray-600 whitespace-nowrap">
                  {filteredMessages.length} of {messages.length} messages
                </div>
              </div>
            </div>

            {filtersExpanded && (
              <div className={cn(layout.isPhonePortrait && 'max-h-[40vh] overflow-y-auto')}>
                {/* Filter Status */}
                <div className="flex items-center space-x-4 text-xs text-gray-600">
                  <span className="font-bold text-gray-800">
                    {selectedMessageTypes.size} of {MESSAGE_TYPES.length} message types selected
                    {selectedMessageTypes.size < MESSAGE_TYPES.length && (
                      <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                        Server Filter Active
                      </span>
                    )}
                  </span>
                  {selectedMessageTypes.has('SCHD') && (
                    <span className="font-bold text-purple-800">
                      {selectedSensorPorts.size} of {SENSOR_PORTS.length} sensor ports selected
                    </span>
                  )}
                </div>

                {/* Message Type Checkboxes - Blue background when selected */}
                <div className="flex flex-wrap gap-1">
                  {MESSAGE_TYPES.map((messageType) => {
                    const isSelected = selectedMessageTypes.has(messageType)
                    const isFoundInResults = foundMessageTypes.has(messageType)

                    return (
                      <label
                        key={messageType}
                        className={cn(
                          'flex items-center cursor-pointer rounded px-2 py-1 text-xs transition-all border',
                          isSelected
                            ? 'border-blue-300 bg-blue-50 hover:bg-blue-100'
                            : 'border-gray-200 bg-gray-50 hover:bg-gray-100',
                          !isFoundInResults && 'opacity-50'
                        )}
                        title={!isFoundInResults ? 'No messages of this type in current results' : ''}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleMessageType(messageType)}
                          className="sr-only"
                        />
                        <span
                          className={cn(
                            'inline-block w-4 h-4 rounded mr-1 text-xs flex items-center justify-center font-mono',
                            isSelected ? 'bg-blue-100 text-blue-800' : 'bg-gray-200 text-gray-600'
                          )}
                        >
                          {isSelected ? '✓' : ''}
                        </span>
                        <span className={cn('font-medium', isSelected ? 'text-blue-900' : 'text-gray-700')}>
                          {messageType}
                        </span>
                      </label>
                    )
                  })}
                </div>

                {/* Sensor Port Checkboxes - Purple background when selected */}
                {selectedMessageTypes.has('SCHD') && (
                  <div className="flex flex-wrap gap-1">
                    {SENSOR_PORTS.map((sensorPort) => {
                      const isSchdInResults = foundMessageTypes.has('SCHD')
                      const isDisabled = !isSchdInResults

                      return (
                        <label
                          key={sensorPort}
                          className={cn(
                            'flex items-center rounded px-2 py-1 text-xs transition-all border',
                            isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                            selectedSensorPorts.has(sensorPort)
                              ? 'border-purple-300 bg-purple-50'
                              : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                          )}
                          title={isDisabled ? 'No SCHD messages in current results' : ''}
                        >
                          <input
                            type="checkbox"
                            checked={selectedSensorPorts.has(sensorPort)}
                            onChange={() => toggleSensorPort(sensorPort)}
                            disabled={isDisabled}
                            className="sr-only"
                          />
                          <span
                            className={cn(
                              'inline-block w-4 h-4 rounded mr-1 text-xs flex items-center justify-center font-mono',
                              selectedSensorPorts.has(sensorPort)
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-gray-200 text-gray-600'
                            )}
                          >
                            ✓
                          </span>
                          <span
                            className={cn(
                              'font-medium',
                              selectedSensorPorts.has(sensorPort) ? 'text-purple-900' : 'text-gray-700'
                            )}
                          >
                            {sensorPort}
                          </span>
                        </label>
                      )
                    })}
                  </div>
                )}

                {/* Unit overrides - moved from table header for better space usage */}
                {presentUnitCategories.length > 0 && (
                  <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
                    <span className="text-xs text-gray-600 font-medium whitespace-nowrap">Units:</span>
                    <UnitCategoryDropdowns
                      presentCategories={presentUnitCategories}
                      unitOverrides={unitOverrides}
                      onUnitChange={setUnitOverride}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Messages Table Section */}
      <div className="flex-1">
        {sigfoxIdFromUrl && !isValidSigfoxId(sigfoxIdFromUrl) ? (
          <div className="h-full bg-white rounded-lg shadow flex items-center justify-center">
            <div className="text-center">
              <p className="text-red-500 mb-2">Invalid sigfoxId format</p>
              <p className="text-sm text-gray-400">sigfoxId must be a 6-character hexadecimal string (e.g., 1A2B3C)</p>
            </div>
          </div>
        ) : !selectedDevice ? (
          <div className="h-full bg-white rounded-lg shadow flex items-center justify-center">
            <div className="text-center">
              <p className="text-gray-500 mb-2">No device selected</p>
              <p className="text-sm text-gray-400">Select a device from the device list to view messages</p>
            </div>
          </div>
        ) : isLoading ? (
          <div className="h-full bg-white rounded-lg shadow flex items-center justify-center">
            <p className="text-gray-500">Loading messages...</p>
          </div>
        ) : error ? (
          <div className="h-full bg-white rounded-lg shadow flex items-center justify-center">
            <p className="text-red-500">Error loading messages: {error}</p>
          </div>
        ) : filteredMessages.length === 0 ? (
          <div className="h-full bg-white rounded-lg shadow flex items-center justify-center">
            <p className="text-gray-500">
              {messages.length === 0 ? 'No messages found for this device' : 'No messages match your filters'}
            </p>
          </div>
        ) : (
          <VirtualizedMessageTable
            messages={filteredMessages}
            className="h-full"
            showTestMessageColumn={false}
            selectedSensorPorts={selectedSensorPorts}
            sensorConfigs={sensorConfigMap}
            onLoadMore={handleLoadMore}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            useViewportHeight={true}
            reservedHeight={reservedHeight}
            sortOrder={sortOrder}
            onSortChange={setSortOrder}
            unitOverrides={unitOverrides}
            presentUnitCategories={[]}
            onUnitOverrideChange={setUnitOverride}
            showRawMessage={layout.isDesktop}
            isMobilePortrait={layout.isPhonePortrait}
          />
        )}
      </div>
    </div>
  )
}

interface RefreshControlsProps {
  canRefresh: boolean
  isRefreshing: boolean
  isLive: boolean
  onRefresh: () => void
  onLiveChange: (live: boolean) => void
  lastChecked: { at: number; added: number } | null
}

// "Refresh" checks once for new messages; "Live" keeps checking every minute
function RefreshControls({ canRefresh, isRefreshing, isLive, onRefresh, onLiveChange, lastChecked }: RefreshControlsProps) {
  const disabledTitle = 'New messages only show for a range that ends today'
  return (
    <div className="flex items-center gap-2 text-xs">
      {lastChecked && canRefresh && (
        <span className="text-gray-500 whitespace-nowrap" title={`Checked ${new Date(lastChecked.at).toLocaleTimeString()}`}>
          {lastChecked.added > 0
            ? `${lastChecked.added} new`
            : `Up to date · ${new Date(lastChecked.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`}
        </span>
      )}
      <button
        type="button"
        onClick={onRefresh}
        disabled={!canRefresh || isRefreshing}
        title={canRefresh ? 'Check for new messages' : disabledTitle}
        className={cn(
          'inline-flex items-center gap-1.5 px-2 py-1 rounded border font-medium whitespace-nowrap',
          canRefresh ? 'border-gray-300 text-gray-700 hover:bg-gray-50' : 'border-gray-200 text-gray-400 cursor-not-allowed'
        )}
      >
        <FontAwesomeIcon icon={faRotateRight} className={cn('h-3 w-3', isRefreshing && 'animate-spin')} />
        Refresh
      </button>
      <label
        className={cn('inline-flex items-center gap-1.5 whitespace-nowrap', canRefresh ? 'cursor-pointer text-gray-700' : 'cursor-not-allowed text-gray-400')}
        title={canRefresh ? 'Check for new messages every minute' : disabledTitle}
      >
        <button
          type="button"
          role="switch"
          aria-checked={isLive && canRefresh}
          disabled={!canRefresh}
          onClick={() => onLiveChange(!isLive)}
          className={cn(
            'relative inline-flex h-4 w-7 flex-shrink-0 rounded-full transition-colors',
            isLive && canRefresh ? 'bg-green-500' : 'bg-gray-300'
          )}
        >
          <span
            className={cn(
              'absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform',
              isLive && canRefresh ? 'translate-x-3.5' : 'translate-x-0.5'
            )}
          />
        </button>
        Live
        {isLive && canRefresh && <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" aria-hidden="true" />}
      </label>
    </div>
  )
}
